import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Button,
  Typography,
  MenuItem,
  Alert,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import Slide from '@mui/material/Slide';
import CloseIcon from '@mui/icons-material/Close';
import { CustomTextField } from 'components/shared';
import { ListDropdown } from 'api/common.types';
import { GetAllInstitutionQuery } from 'api/institutions/dtos/get-all-institution.query.dto';
import { formatCNPJ, validateCNPJ } from 'services/utils/cnpj.utils';
import { isArray } from 'lodash';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction='up' ref={ref} {...props} />;
});

interface FiltersDialogProps {
  open: boolean;
  onClose: () => void;
  initialFilter: Partial<GetAllInstitutionQuery>;
  applyFilters: (
    newFilters: Partial<GetAllInstitutionQuery>,
  ) => Promise<boolean>;
  profilesDropdown: ListDropdown[];
}

const FiltersDialog: React.FC<FiltersDialogProps> = ({
  open,
  onClose,
  initialFilter,
  applyFilters,
  profilesDropdown,
}) => {
  const [filter, setFilter] =
    useState<Partial<GetAllInstitutionQuery>>(initialFilter);
  const [cnpjError, setCnpjError] = useState<string>('');
  const [isConfirmDisabled, setIsConfirmDisabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (open) {
      setFilter(initialFilter);
      setCnpjError('');
      setIsConfirmDisabled(false);
      setErrorMessage('');
    }
  }, [open, initialFilter]);

  const handleConfirm = async () => {
    setErrorMessage('');
    setIsConfirmDisabled(false);
    const filtersAppliedSuccessfully = await applyFilters(filter);
    if (!filtersAppliedSuccessfully) {
      setErrorMessage(
        'Não foi possível localizar nenhum resultado com os dados informados.',
      );
    } else {
      setErrorMessage('');
      onClose();
    }
  };

  const handleClear = () => {
    setFilter({});
    setCnpjError('');
    setIsConfirmDisabled(false);
    setErrorMessage('');
  };

  const handleIdentificatiOnChange = (event: any) => {
    const rawValue = event.target.value.replace(/\D/g, '');
    const formattedValue = formatCNPJ(rawValue);

    if (rawValue) {
      setFilter({ ...filter, identification: rawValue });
      if (rawValue.length === 14 && !validateCNPJ(formattedValue)) {
        setCnpjError('Informe o CNPJ corretamente');
        setIsConfirmDisabled(true);
      } else {
        setCnpjError('');
        setIsConfirmDisabled(false);
      }
    } else {
      setCnpjError('');
      setIsConfirmDisabled(false);
      const { identification, ...rest } = filter;
      setFilter(rest);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      TransitionComponent={Transition}
      sx={{
        '& .MuiPaper-root': {
          height: 'auto',
          width: '100%',
          position: 'fixed',
          bottom: 0,
          margin: 0,
          display: 'flex',
          alignItems: 'center',
        },
      }}
    >
      <Box sx={{ width: '1200px', marginTop: '60px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box
            sx={{
              width: '80px',
              height: '5px',
              borderRadius: '100px 0px 0px 0px',
              backgroundColor: '#00000026',
            }}
          />
        </Box>

        <DialogTitle sx={{ padding: 0, marginBottom: '15px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography
              sx={{
                fontFamily: 'Roboto',
                fontSize: '24px',
                fontWeight: 500,
                lineHeight: '38.4px',
                letterSpacing: '0.15px',
                textAlign: 'left',
              }}
            >
              Filtrar
            </Typography>
            <Button startIcon={<CloseIcon />} onClick={handleClear}>
              LIMPAR
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ padding: 0 }}>
          {errorMessage && (
            <Box sx={{ margin: '20px 0px 10px 0px' }}>
              <Alert
                severity='error'
                sx={{ position: 'relative !important', height: '48px' }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Roboto',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '20.02px',
                    letterSpacing: '0.17px',
                    textAlign: 'left',
                  }}
                >
                  {errorMessage}
                </Typography>
              </Alert>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
              <CustomTextField
                label='Nome (Opcional)'
                variant='outlined'
                size='small'
                fullWidth
                value={filter.name ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) {
                    setFilter({ ...filter, name: value.toUpperCase() });
                  } else {
                    const { name, ...rest } = filter;
                    setFilter(rest);
                  }
                }}
                sx={{
                  marginTop: '10px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    legend: {
                      width: '85px',
                    },
                  },
                }}
                InputLabelProps={{
                  sx: {
                    left: '5px',
                    top: '5px',
                    fontFamily: 'Roboto',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '12px',
                    letterSpacing: '0.15px',
                    color: '#00000099',
                  },
                }}
              />

              <CustomTextField
                label='CNPJ (Opcional)'
                variant='outlined'
                size='small'
                fullWidth
                value={filter.identification ?? ''}
                onChange={handleIdentificatiOnChange}
                onBlur={handleIdentificatiOnChange}
                inputProps={{ maxLength: 18 }}
                error={!!cnpjError}
                helperText={cnpjError}
                sx={{
                  marginTop: '10px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    legend: {
                      width: '75px',
                    },
                  },
                }}
                InputLabelProps={{
                  sx: {
                    left: '5px',
                    top: '5px',
                    fontFamily: 'Roboto',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '12px',
                    letterSpacing: '0.15px',
                    color: '#00000099',
                  },
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
              <CustomTextField
                label='Município (Opcional)'
                variant='outlined'
                size='small'
                fullWidth
                value={filter.municipality ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) {
                    setFilter({ ...filter, municipality: value.toUpperCase() });
                  } else {
                    const { municipality, ...rest } = filter;
                    setFilter(rest);
                  }
                }}
                sx={{
                  marginTop: '10px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    legend: {
                      width: '85px',
                    },
                  },
                }}
                InputLabelProps={{
                  sx: {
                    left: '5px',
                    top: '5px',
                    fontFamily: 'Roboto',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '12px',
                    letterSpacing: '0.15px',
                    color: '#00000099',
                  },
                }}
              />

              <CustomTextField
                label='Perfil (Opcional)'
                variant='outlined'
                size='small'
                select
                fullWidth
                value={filter.profileUuid ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setFilter({ ...filter, profileUuid: value || undefined });
                }}
                sx={{
                  marginTop: '10px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    legend: {
                      width: '80px',
                    },
                  },
                }}
                InputLabelProps={{
                  sx: {
                    left: '5px',
                    top: '5px',
                    fontFamily: 'Roboto',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '12px',
                    letterSpacing: '0.15px',
                    color: '#00000099',
                  },
                }}
              >
                {isArray(profilesDropdown) &&
                  profilesDropdown.map((profile) => (
                    <MenuItem
                      key={profile.uuid}
                      value={profile.uuid}
                      sx={{
                        fontFamily: 'Roboto',
                        fontSize: '16px',
                        fontWeight: 400,
                        lineHeight: '24px',
                        letterSpacing: '0.15px',
                        textAlign: 'left',
                      }}
                    >
                      {profile.name}
                    </MenuItem>
                  ))}
              </CustomTextField>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{ justifyContent: 'flex-start', padding: 0, margin: '30px 0px' }}
        >
          <Button
            variant='contained'
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
          >
            Aplicar Filtros
          </Button>
          <Button variant='outlined' onClick={onClose}>
            Cancelar
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default FiltersDialog;
